import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  CreditCard,
  ExternalLink,
  Eye,
  EyeOff,
  Landmark,
  Layers,
  RefreshCw,
  Send,
  ShieldCheck,
  Wallet,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { walletSendReceiveActivities, getMyOrders, getMyOrdersAsBuyer, getMyApproval, getCustomOffers } from "@/utils/services/user.service";
import {
  SidebarOverlay,
  SidebarContainer,
  SidebarPanel,
  NeonGlow,
  SidebarHeader,
  HeaderLeft,
  WalletIconContainer,
  HeaderTitle,
  HeaderSubtitle,
  HeaderActions,
  SidebarIconButton,
  SidebarBody,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionHint,
  BalanceCard as BalanceCardStyled,
  BalanceCardHeader,
  BalanceCardLeft,
  WalletIconBox,
  BalanceInfo,
  BalanceLabel,
  BalanceSubtext,
  BalanceCardRight,
  BalanceAmount,
  BalanceValue,
  BalanceCurrency,
  Divider,
  TokenBreakdown,
  TokenBreakdownHeader,
  TokenBreakdownTitle,
  TokenList,
  TokenItem,
  TokenSymbol,
  TokenValue,
  ActionButtons,
  PrimaryPill,
  TabContainer,
  TabButton,
  FilterContainer,
  FilterButton,
  ActivityList,
  ActivityRow,
  ActivityContent,
  ActivityLeft,
  WalletIconBadge,
  ActivityDetails,
  ActivityTitleRow,
  ActivityTitle,
  StatusBadge,
  ActivitySubtitle,
  ActivityMeta,
  ActivityRight,
  ActivityAmount,
  ActivityActions,
  OrderRow as OrderRowStyled,
  OrderContent,
  OrderLeft,
  OrderTitleRow,
  OrderTitle,
  OrderStatusBadge,
  OrderSubtitle,
  OrderMeta,
  OrderRight,
  OrderTotal,
  OrderBreakdown,
  OrderActions,
  EmptyState,
  TipBox,
  TipHighlight,
  SidebarFooter,
  FooterContent,
  FooterActions,
  FooterText,
  OrderDropdownContainer,
  OrderDropdownButton,
  OrderDropdownMenu,
  OrderDropdownItem,
} from './WalletSidebarStyles';

// -----------------------------
// Mock data
// -----------------------------

const mockBalances = [
  {
    kind: "virtual",
    label: "Virtual Wallet",
    totalFiat: 12840.22,
    fiatCcy: "EUR",
    tokens: [
      { symbol: "USDT", value: 4820.5 },
      { symbol: "CTY", value: 315000 },
    ],
  },
  {
    kind: "onchain",
    label: "On-chain Wallet",
    totalFiat: 5940.12,
    fiatCcy: "EUR",
    tokens: [
      { symbol: "BNB", value: 3.42 },
      { symbol: "USDT", value: 1120.0 },
    ],
  },
  {
    kind: "bank",
    label: "Bank Wallet",
    totalFiat: 22310.0,
    fiatCcy: "EUR",
    tokens: [
      { symbol: "EUR", value: 22310.0 },
      { symbol: "USD", value: 1450.0 },
    ],
  },
];

const mockActivities = [
  {
    id: "a1",
    wallet: "onchain",
    title: "Sent USDT",
    subtitle: "To 0x8c…a21",
    amount: -250,
    currency: "USDT",
    ts: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
    status: "completed",
    hash: "0x9f1b…e7c1",
  },
  {
    id: "a2",
    wallet: "virtual",
    title: "Received",
    subtitle: "Internal transfer",
    amount: 1200,
    currency: "EUR",
    ts: new Date(Date.now() - 1000 * 60 * 55).toISOString(),
    status: "completed",
  },
  {
    id: "a3",
    wallet: "bank",
    title: "Card authorization",
    subtitle: "Merchant: IT*Services",
    amount: -89.99,
    currency: "EUR",
    ts: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    status: "pending",
  },
  {
    id: "a4",
    wallet: "virtual",
    title: "Swap CTY → USDT",
    subtitle: "Rate 0.00xx",
    amount: -15000,
    currency: "CTY",
    ts: new Date(Date.now() - 1000 * 60 * 60 * 22).toISOString(),
    status: "completed",
  },
];

const mockOrders = [
  {
    id: "o-10492",
    title: "Bulk Paracetamol 500mg — 10k units",
    subtitle: "Auction: PharmaCrowd Lot #22",
    qty: 10000,
    unitPrice: 0.087,
    currency: "EUR",
    total: 870,
    ts: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
    status: "processing",
  },
  {
    id: "o-10491",
    title: "Rolex Submariner 2020 — Verified",
    subtitle: "Seller: Vault Partner",
    qty: 1,
    unitPrice: 9850,
    currency: "EUR",
    total: 9850,
    ts: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    status: "shipped",
  },
  {
    id: "o-10477",
    title: "GPU Lot — RTX Series",
    subtitle: "Distributor: EU Wholesale",
    qty: 12,
    unitPrice: 690,
    currency: "EUR",
    total: 8280,
    ts: new Date(Date.now() - 1000 * 60 * 60 * 30).toISOString(),
    status: "delivered",
  },
];

// -----------------------------
// Helpers
// -----------------------------

function formatMoney(n, ccy) {
  try {
    // For USD, show only $ without US prefix
    if (ccy === "USD" || ccy === "US$") {
      const sign = n < 0 ? "-" : "";
      const abs = Math.abs(n);
      return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n);
  } catch {
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(n);
    return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${ccy}`;
  }
}

function formatToken(n) {
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  if (m > 0) return `${m}m ago`;
  return `${s}s ago`;
}

function walletIcon(kind) {
  if (kind === "virtual") return <Layers size={16} />;
  if (kind === "onchain") return <Wallet size={16} />;
  return <Landmark size={16} />;
}

// -----------------------------
// Balance card component
// -----------------------------

function BalanceCardComponent({
  w,
  reveal,
  expanded,
  onToggle,
  onToggleReveal,
  actions,
}) {
  return (
    <BalanceCardStyled>
      <BalanceCardHeader onClick={onToggle}>
        <BalanceCardLeft>
          <WalletIconBox>
            {walletIcon(w.kind)}
          </WalletIconBox>
          <BalanceInfo>
            <BalanceLabel>{w.label}</BalanceLabel>
            <BalanceSubtext>Total balance</BalanceSubtext>
          </BalanceInfo>
        </BalanceCardLeft>
        <BalanceCardRight>
          <BalanceAmount>
            <BalanceValue>{reveal ? formatMoney(w.totalFiat, w.fiatCcy) : "••••••"}</BalanceValue>
            <BalanceCurrency>{w.fiatCcy}</BalanceCurrency>
          </BalanceAmount>
          {expanded ? <ChevronDown size={16} style={{ color: 'rgba(255, 255, 255, 0.6)' }} /> : <ChevronRight size={16} style={{ color: 'rgba(255, 255, 255, 0.6)' }} />}
        </BalanceCardRight>
      </BalanceCardHeader>

      {expanded && (
        <TokenBreakdown>
          <TokenBreakdownHeader>
            <TokenBreakdownTitle>Token breakdown</TokenBreakdownTitle>
            {/* <SidebarIconButton title={reveal ? "Hide balances" : "Reveal balances"} onClick={onToggleReveal}>
              {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
            </SidebarIconButton> */}
          </TokenBreakdownHeader>

          <TokenList>
            {(w.tokens ?? []).map((t) => (
              <TokenItem key={t.symbol}>
                <TokenSymbol>{t.symbol}</TokenSymbol>
                <TokenValue>{reveal ? formatToken(t.value) : "••••"}</TokenValue>
              </TokenItem>
            ))}
          </TokenList>

          <ActionButtons>
            <PrimaryPill onClick={() => actions?.onSend?.(w.kind)}>
              <Send size={16} /> Send
            </PrimaryPill>
            <PrimaryPill onClick={() => actions?.onReceive?.(w.kind)}>
              <CreditCard size={16} /> Receive
            </PrimaryPill>
          </ActionButtons>
        </TokenBreakdown>
      )}
    </BalanceCardStyled>
  );
}

// -----------------------------
// Activity Row component
// -----------------------------

function ActivityRowComponent({ a, idx, actions }) {
  const isOut = a.amount < 0;
  const amountText = `${isOut ? "-" : "+"}${formatToken(Math.abs(a.amount))} ${a.currency}`;

  return (
    <ActivityRow>
      <ActivityContent>
        <ActivityLeft>
          <WalletIconBadge kind={a.wallet}>
            {walletIcon(a.wallet)}
          </WalletIconBadge>
          <ActivityDetails>
            <ActivityTitleRow>
              <ActivityTitle>{a.title}</ActivityTitle>
              <StatusBadge status={a.status}>
                {a.status.toUpperCase()}
              </StatusBadge>
            </ActivityTitleRow>
            <ActivitySubtitle>{a.subtitle}</ActivitySubtitle>
            <ActivityMeta>
              <span>{timeAgo(a.ts)}</span>
              <span>•</span>
              <span style={{ textTransform: 'uppercase' }}>{a.wallet}</span>
              {a.hash ? (
                <>
                  <span>•</span>
                  <span style={{ fontFamily: 'monospace' }}>{a.hash}</span>
                </>
              ) : null}
            </ActivityMeta>
          </ActivityDetails>
        </ActivityLeft>

        <ActivityRight>
          <ActivityAmount isOut={isOut}>{amountText}</ActivityAmount>
          <ActivityActions>
            {a.hash ? (
              <SidebarIconButton title="Open explorer" onClick={() => actions?.onOpenExplorer?.(a.hash)}>
                <ExternalLink size={16} />
              </SidebarIconButton>
            ) : null}
            <SidebarIconButton
              title="Copy reference"
              onClick={() => {
                const v = a.hash ?? a.counterparty ?? a.id;
                actions?.onCopy?.(v);
                navigator.clipboard?.writeText(v);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy size={16} />
            </SidebarIconButton>
          </ActivityActions>
        </ActivityRight>
      </ActivityContent>
    </ActivityRow>
  );
}

// -----------------------------
// Order Row component
// -----------------------------

function OrderRowComponent({ o, idx, actions }) {
  return (
    <OrderRowStyled onClick={() => actions?.onOpenOrder?.(o)}>
      <OrderContent>
        <OrderLeft>
          <OrderTitleRow>
            <OrderTitle>{o.title}</OrderTitle>
            <OrderStatusBadge status={o.status}>
              {o.status.toUpperCase()}
            </OrderStatusBadge>
          </OrderTitleRow>
          <OrderSubtitle>{o.subtitle}</OrderSubtitle>
          <OrderMeta>
            <span>{timeAgo(o.ts)}</span>
            <span>•</span>
            <span style={{ fontFamily: 'monospace' }}>{o.id}</span>
          </OrderMeta>
        </OrderLeft>

        <OrderRight>
          <OrderTotal>{formatMoney(o.total, o.currency)}</OrderTotal>
          <OrderBreakdown>
            {o.qty} × {formatToken(o.unitPrice)} {o.currency}
          </OrderBreakdown>
          <OrderActions>
            <SidebarIconButton
              title="Copy Order ID"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                actions?.onCopy?.(o.id);
                navigator.clipboard?.writeText(o.id);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy size={16} />
            </SidebarIconButton>
          </OrderActions>
        </OrderRight>
      </OrderContent>
    </OrderRowStyled>
  );
}

// -----------------------------
// Main Sidebar component
// -----------------------------

export function WalletSidebar({
  open,
  onOpenChange,
  balances = mockBalances,
  activities: propActivities,
  orders = mockOrders,
  maxActivities = 8,
  maxOrders = 6,
  title = "Wallet Summary",
  subtitle = "Virtual • On-chain • Bank",
  actions,
  onRefresh,
}) {
  const history = useHistory();
  const [reveal, setReveal] = useState(false);
  const [mode, setMode] = useState("activity");
  const [tab, setTab] = useState("all");
  const [expanded, setExpanded] = useState({ virtual: false, onchain: false, bank: false });
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [orderType, setOrderType] = useState("seller");
  const [fetchedOrders, setFetchedOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);
  const orderDropdownRef = useRef(null);
  const panelRef = useRef(null);

  // Map wallet filter to trx_type for API
  const getTrxType = (filter) => {
    if (filter === "all") return "";
    if (filter === "virtual") return "virtual";
    if (filter === "onchain") return "onchain";
    if (filter === "bank") return "bank";
    return "";
  };

  // Fetch activities from API
  useEffect(() => {
    if (!open || mode !== "activity") return;

    const fetchActivities = async () => {
      setIsLoadingActivities(true);
      try {
        const requestData = {
          limit: maxActivities,
          page_number: 1,
          symbol: "",
          trx_type: getTrxType(tab),
        };

        const response = await walletSendReceiveActivities(requestData);

        if (response?.data?.data) {
          // Transform API response to activities format
          const userData = JSON.parse(localStorage.getItem("user"))?.data?.user || {};
          const userAddress = userData.address || "";

          const transformedActivities = response.data.data.map((item) => {
            // Determine wallet type from trx_type
            let wallet = "virtual";
            if (item.trx_type === "onchain" || item.trx_type === "on-chain") {
              wallet = "onchain";
            } else if (item.trx_type === "bank") {
              wallet = "bank";
            } else if (item.trx_type === "virtual") {
              wallet = "virtual";
            }

            // Determine direction and amount
            let amount = parseFloat(item.Amount) || 0;
            let isOut = false;

            if (item.Transfer) {
              isOut = item.Transfer.toLowerCase() === userAddress.toLowerCase();
            } else if (item.type) {
              if (item.type.includes("send") || item.type.includes("withdraw")) {
                isOut = true;
              }
            }

            if (isOut) {
              amount = -Math.abs(amount);
            } else {
              amount = Math.abs(amount);
            }

            // Determine status
            let status = "completed";
            if (item.status) {
              status = item.status.toLowerCase();
            } else if (item.Status) {
              status = item.Status.toLowerCase();
            }

            // Create title based on transaction type
            let title = "Transaction";
            if (item.type) {
              if (item.type.includes("send")) {
                title = `Sent ${item.Symbol || ""}`;
              } else if (item.type.includes("receive") || item.type.includes("deposit")) {
                title = "Received";
              } else {
                title = item.type;
              }
            } else if (isOut) {
              title = `Sent ${item.Symbol || ""}`;
            } else {
              title = "Received";
            }

            // Create subtitle
            let subtitle = "";
            if (item.Transfer && !isOut) {
              subtitle = `From ${item.Transfer.slice(0, 6)}...${item.Transfer.slice(-4)}`;
            } else if (item.Transfer && isOut) {
              subtitle = `To ${item.Transfer.slice(0, 6)}...${item.Transfer.slice(-4)}`;
            } else if (item.Name) {
              subtitle = item.Name;
            } else {
              subtitle = isOut ? "External transfer" : "Internal transfer";
            }

            return {
              id: item.id || item._id || `${item.hash || item.Hash || Date.now()}-${Math.random()}`,
              wallet: wallet,
              title: title.trim(),
              subtitle: subtitle,
              amount: amount,
              currency: item.Symbol || item.symbol || "USD",
              ts: item.Date || item.date || new Date().toISOString(),
              status: status,
              hash: item.hash || item.Hash || "",
            };
          });

          setActivities(transformedActivities);
        } else {
          setActivities([]);
        }
      } catch (error) {
        console.error("Error fetching wallet activities:", error);
        setActivities([]);
        toast.error("Failed to fetch wallet activities");
      } finally {
        setIsLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [open, mode, tab, maxActivities, refreshTrigger]);

  const activityList = useMemo(() => {
    // Use fetched activities if available, otherwise use prop activities or mock
    const base = activities.length > 0 ? activities : [];
    const sorted = [...base].sort((a, b) => {
      const dateA = new Date(a.ts).getTime();
      const dateB = new Date(b.ts).getTime();
      return dateB - dateA; // Most recent first
    });
    return sorted.slice(0, maxActivities);
  }, [activities, propActivities, maxActivities]);

  // Fetch orders from API based on orderType
  useEffect(() => {
    if (!open || mode !== "orders") return;

    const fetchOrders = async () => {
      setIsLoadingOrders(true);
      try {
        let response;
        const payload = {
          limit: 10,
          pageNumber: 1,
          orderStatus: 'all',
        };

        switch (orderType) {
          case "seller":
            response = await getMyOrders(payload);
            break;
          case "buyer":
            response = await getMyOrdersAsBuyer(payload);
            break;
          case "approver":
            response = await getMyApproval({ ...payload, approver_status: 'all' });
            break;
          case "custom":
            response = await getCustomOffers({ page_number: 1, limit: maxOrders, offer_status: 'all' });
            break;
          default:
            response = await getMyOrders(payload);
        }

        if (response?.data?.data) {
          const transformedOrders = response.data.data.map((item) => {
            // Extract order details based on order type and API response shape
            let title = "";
            let subtitle = "";
            let qty = 0;
            let unitPrice = 0;
            let total = 0;
            let currency = "EUR";
            const status = item.order_status || item.status || item.offer_status || "new";
            const orderId = item.order_id || item._id || item.offer_id || "";

            let primaryProduct = {};
            let productId = "";

            if (orderType === "custom") {
              // Custom offers (getMyCustomOffer)
              primaryProduct =
                (Array.isArray(item.product_details) && item.product_details[0]) || {};

              const productTitle =
                primaryProduct.title ||
                item.product_name ||
                item.title ||
                "Custom Offer";
              
              productId = item.product_id || item.productId || primaryProduct.product_id || primaryProduct._id || item.store_id || "";

              const storeName =
                Array.isArray(item.store_detail) && item.store_detail[0]
                  ? item.store_detail[0].storeName
                  : "";

              const buyerName =
                Array.isArray(item.buyer_detail) && item.buyer_detail[0]
                  ? item.buyer_detail[0].name
                  : "";

              title = productTitle;
              if (storeName && buyerName) {
                subtitle = `${storeName} • Buyer: ${buyerName}`;
              } else if (storeName) {
                subtitle = `Store: ${storeName}`;
              } else if (buyerName) {
                subtitle = `Buyer: ${buyerName}`;
              } else {
                subtitle = "Custom offer";
              }

              qty = parseFloat(
                primaryProduct.quantity ||
                item.total_quantity ||
                item.quantity ||
                item.qty ||
                0
              );

              // For custom offers, custom_offer_amount is the negotiated total
              total = parseFloat(item.custom_offer_amount || item.total_amount || 0);

              if (qty > 0) {
                unitPrice =
                  parseFloat(primaryProduct.price || item.orignal_price || 0) ||
                  total / qty ||
                  0;
              } else {
                unitPrice = parseFloat(primaryProduct.price || item.orignal_price || 0);
              }

              currency = item.symbol || "USDT (BEP20)";
            } else if (orderType === "seller" || orderType === "buyer") {
              // Orders as Seller / Buyer – use product_details and order info
              primaryProduct =
                (Array.isArray(item.product_details) && item.product_details[0]) || {};

              const productTitle =
                primaryProduct.title ||
                item.product_name ||
                item.title ||
                "Order";
              
              productId = item.product_id || item.productId || primaryProduct.product_id || primaryProduct._id || item.store_id || "";

              const buyerName =
                Array.isArray(item.buyer_detail) && item.buyer_detail[0]
                  ? item.buyer_detail[0].name
                  : "";

              title = productTitle;
              subtitle = buyerName
                ? `Buyer: ${buyerName}`
                : `Order #${item.order_transaction_number || orderId}`;

              qty = parseFloat(
                primaryProduct.quantity ||
                item.total_quantity ||
                item.quantity ||
                item.qty ||
                0
              );

              total = parseFloat(item.total_amount || item.order_total || 0);

              if (qty > 0) {
                unitPrice =
                  parseFloat(primaryProduct.price || 0) ||
                  total / qty ||
                  0;
              } else {
                unitPrice = parseFloat(primaryProduct.price || 0);
              }

              // Use token symbol (e.g. "USDT (BEP20)") for breakdown text
              currency = item.symbol || "USDT (BEP20)";
            } else if (orderType === "approver") {
              // Orders that this user can approve (getOrderForMyApproval)
              primaryProduct =
                (Array.isArray(item.product_details) && item.product_details[0]) || {};

              const productTitle =
                primaryProduct.title ||
                item.product_name ||
                item.title ||
                "Order to Approve";
              
              productId = item.product_id || item.productId || primaryProduct.product_id || primaryProduct._id || item.store_id || "";

              const storeName =
                Array.isArray(item.store_detail) && item.store_detail[0]
                  ? item.store_detail[0].storeName
                  : "";

              const buyerName =
                Array.isArray(item.buyer_detail) && item.buyer_detail[0]
                  ? item.buyer_detail[0].name
                  : "";

              title = productTitle;
              if (storeName && buyerName) {
                subtitle = `${storeName} • Buyer: ${buyerName}`;
              } else if (storeName) {
                subtitle = `Store: ${storeName}`;
              } else if (buyerName) {
                subtitle = `Buyer: ${buyerName}`;
              } else {
                subtitle = "Awaiting your approval";
              }

              qty = parseFloat(
                primaryProduct.quantity ||
                item.total_quantity ||
                item.quantity ||
                item.qty ||
                0
              );

              total = parseFloat(item.total_amount || item.order_total || 0);

              if (qty > 0) {
                unitPrice =
                  parseFloat(primaryProduct.price || 0) ||
                  total / qty ||
                  0;
              } else {
                unitPrice = parseFloat(primaryProduct.price || 0);
              }

              currency = item.symbol || "USDT (BEP20)";
            } else {
              // Approver / fallback
              title = item.product_name || item.title || item.product_title || "Order";
              subtitle = item.auction_name || item.store_name || item.description || "";
              qty = parseFloat(item.quantity || item.qty || 0);
              unitPrice = parseFloat(item.unit_price || item.price || item.product_price || 0);
              total = parseFloat(item.total_amount || item.total || item.order_total || 0);
              currency = item.currency || item.order_currency || "EUR";
              productId = item.product_id || item.productId || item.store_id || "";
            }

            return {
              id: orderId,
              title,
              subtitle,
              qty,
              unitPrice,
              currency,
              total,
              productId,
              productTitle: title,
              ts:
                item.createdAt ||
                item.created_at ||
                item.date ||
                item.order_date ||
                new Date().toISOString(),
              status: String(status).toLowerCase(),
            };
          });

          setFetchedOrders(transformedOrders);
        } else {
          setFetchedOrders([]);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        setFetchedOrders([]);
        toast.error("Failed to fetch orders");
      } finally {
        setIsLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [open, mode, orderType, maxOrders]);

  const orderList = useMemo(() => {
    // Use fetched orders if available, otherwise use prop orders or mock
    const base = fetchedOrders.length > 0 ? fetchedOrders : (orders || mockOrders);
    const sorted = [...base].sort((a, b) => {
      const dateA = new Date(a.ts).getTime();
      const dateB = new Date(b.ts).getTime();
      return dateB - dateA; // Most recent first
    });
    return sorted.slice(0, maxOrders);
  }, [fetchedOrders, orders, maxOrders]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (orderDropdownRef.current && !orderDropdownRef.current.contains(event.target)) {
        setIsOrderDropdownOpen(false);
      }
    };

    if (isOrderDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOrderDropdownOpen]);

  useEffect(() => {
    function onKey(e) {
      if (!open) return;
      if (e.key === "Escape") onOpenChange(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => panelRef.current?.focus?.(), 10);
    return () => window.clearTimeout(t);
  }, [open]);

  // Refetch activities when refresh is triggered
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
    // Trigger activity refetch
    if (open && mode === "activity") {
      setRefreshTrigger(prev => prev + 1);
    }
    toast.info("Refreshing...");
  };

  const handleSendReceive = (kind, type) => {
    // Close sidebar
    onOpenChange(false);
    // Navigate to dashboard
    history.push('/dashboard');
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 200, behavior: 'smooth' });
    }, 100);
  };

  const handleOpenOrder = (order) => {
    // Redirect to getmyorders page
    history.push('/getmyorders');
    onOpenChange(false); // Close sidebar after navigation
  };

  const defaultActions = {
    onSend: (kind) => handleSendReceive(kind, 'send'),
    onReceive: (kind) => handleSendReceive(kind, 'receive'),
    onSecurity: (kind) => toast.info(`Security for ${kind} wallet`),
    onOpenExplorer: (hash) => toast.info(`Open explorer: ${hash}`),
    onOpenOrder: handleOpenOrder,
    onCopy: (v) => {
      navigator.clipboard?.writeText(v);
      toast.success('Copied to clipboard');
    },
    onRefresh: handleRefresh,
    onGoFullWallet: () => toast.info("Go to Full Wallet"),
    onOpenRisk: () => toast.info("Open Risk"),
    ...actions,
  };

  if (!open) return null;

  const sidebarContent = (
    <>
      <SidebarOverlay onClick={() => onOpenChange(false)} />
      <SidebarContainer open={open}>
        <SidebarPanel ref={panelRef} tabIndex={-1}>
          <NeonGlow />

          {/* Header */}
          <SidebarHeader>
            <HeaderLeft>
              <WalletIconContainer>
                <Wallet size={20} style={{ color: 'rgba(255, 255, 255, 0.9)' }} />
              </WalletIconContainer>
              <div>
                <HeaderTitle>{title}</HeaderTitle>
                <HeaderSubtitle>{subtitle}</HeaderSubtitle>
              </div>
            </HeaderLeft>
            <HeaderActions>
              <SidebarIconButton title="Refresh" onClick={defaultActions.onRefresh}>
                <RefreshCw size={16} />
              </SidebarIconButton>
              <SidebarIconButton title={reveal ? "Hide" : "Reveal"} onClick={() => setReveal((s) => !s)}>
                {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
              </SidebarIconButton>
              <SidebarIconButton title="Close" onClick={() => onOpenChange(false)}>
                <X size={16} />
              </SidebarIconButton>
            </HeaderActions>
          </SidebarHeader>

          {/* Body */}
          <SidebarBody>
            <SectionContainer>
              <SectionHeader>
                <SectionTitle>BALANCES</SectionTitle>
                <SectionHint>Tap a card to expand</SectionHint>
              </SectionHeader>

              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {balances.map((w) => (
                  <BalanceCardComponent
                    key={w.kind}
                    w={w}
                    reveal={reveal}
                    expanded={expanded[w.kind]}
                    onToggle={() => setExpanded((p) => ({ ...p, [w.kind]: !p[w.kind] }))}
                    onToggleReveal={() => setReveal((s) => !s)}
                    actions={defaultActions}
                  />
                ))}
              </div>
            </SectionContainer>

            <Divider />

            {/* Latest */}
            <SectionContainer>
              <SectionHeader>
                <SectionTitle>{mode === "activity" ? "LATEST ACTIVITY" : "LATEST ORDERS"}</SectionTitle>
                <SectionHint>{mode === "activity" ? `Max ${maxActivities}` : `Max ${maxOrders}`}</SectionHint>
              </SectionHeader>

              <TabContainer>
                <TabButton active={mode === "activity"} onClick={() => setMode("activity")}>
                  Wallet Movements
                </TabButton>
                <TabButton active={mode === "orders"} onClick={() => setMode("orders")}>
                  Orders
                </TabButton>
              </TabContainer>

              {mode === "orders" && (
                <OrderDropdownContainer ref={orderDropdownRef}>
                  <OrderDropdownButton onClick={() => setIsOrderDropdownOpen(!isOrderDropdownOpen)}>
                    <span>
                      {orderType === "seller" && "As Seller"}
                      {orderType === "buyer" && "As Buyer"}
                      {orderType === "approver" && "By Approver"}
                      {orderType === "custom" && "By Custom Offer"}
                    </span>
                    <ChevronDown size={16} style={{ transform: isOrderDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                  </OrderDropdownButton>
                  {isOrderDropdownOpen && (
                    <OrderDropdownMenu>
                      <OrderDropdownItem
                        active={orderType === "seller"}
                        onClick={() => {
                          setOrderType("seller");
                          setIsOrderDropdownOpen(false);
                        }}
                      >
                        As Seller
                      </OrderDropdownItem>
                      <OrderDropdownItem
                        active={orderType === "buyer"}
                        onClick={() => {
                          setOrderType("buyer");
                          setIsOrderDropdownOpen(false);
                        }}
                      >
                        As Buyer
                      </OrderDropdownItem>
                      <OrderDropdownItem
                        active={orderType === "approver"}
                        onClick={() => {
                          setOrderType("approver");
                          setIsOrderDropdownOpen(false);
                        }}
                      >
                        By Approver
                      </OrderDropdownItem>
                      <OrderDropdownItem
                        active={orderType === "custom"}
                        onClick={() => {
                          setOrderType("custom");
                          setIsOrderDropdownOpen(false);
                        }}
                      >
                        By Custom Offer
                      </OrderDropdownItem>
                    </OrderDropdownMenu>
                  )}
                </OrderDropdownContainer>
              )}

              {mode === "activity" && (
                <FilterContainer>
                  {[
                    { k: "all", label: "All", icon: <Layers size={16} /> },
                    { k: "virtual", label: "Virtual", icon: <Layers size={16} /> },
                    { k: "onchain", label: "On-chain", icon: <Wallet size={16} /> },
                    { k: "bank", label: "Bank", icon: <Landmark size={16} /> },
                  ].map((t) => (
                    <FilterButton
                      key={t.k}
                      active={tab === t.k}
                      onClick={() => setTab(t.k)}
                    >
                      {t.icon}
                      {t.label}
                    </FilterButton>
                  ))}
                </FilterContainer>
              )}
            </SectionContainer>

            {/* List */}
            <ActivityList>
              {mode === "activity" ? (
                <>
                  {isLoadingActivities ? (
                    <EmptyState>Loading activities...</EmptyState>
                  ) : activityList.length === 0 ? (
                    <EmptyState>No Activity found</EmptyState>
                  ) : (
                    activityList.map((a, idx) => (
                      <ActivityRowComponent key={a.id} a={a} idx={idx} actions={defaultActions} />
                    ))
                  )}
                </>
              ) : (
                <>
                  {isLoadingOrders ? (
                    <EmptyState>Loading orders...</EmptyState>
                  ) : orderList.length === 0 ? (
                    <EmptyState>No orders found</EmptyState>
                  ) : (
                    orderList.map((o, idx) => <OrderRowComponent key={o.id} o={o} idx={idx} actions={defaultActions} />)
                  )}
                </>
              )}

            </ActivityList>
            <TipBox>
              {mode != "activity" && (
                <>Tip: Click an order to open the <TipHighlight>Order View</TipHighlight>.</>

              )
              }
            </TipBox>
            {/* Footer */}
            {/* <SidebarFooter>
              <FooterContent>
                <FooterActions>
                  <PrimaryPill onClick={defaultActions.onGoFullWallet}>
                    <Wallet size={16} /> Full Wallet
                  </PrimaryPill>
                  <PrimaryPill onClick={defaultActions.onOpenRisk}>
                    <ShieldCheck size={16} /> Risk
                  </PrimaryPill>
                  <SidebarIconButton title="Close" onClick={() => onOpenChange(false)}>
                    <X size={16} />
                  </SidebarIconButton>
                </FooterActions>
                <FooterText>Custodiy • Wallet Sidebar PRO</FooterText>
              </FooterContent>
            </SidebarFooter> */}
          </SidebarBody>
        </SidebarPanel>
      </SidebarContainer>
    </>
  );

  return ReactDOM.createPortal(sidebarContent, document.body);
}

export default WalletSidebar;
